import {
  Avatar,
  Icon,
  logos,
  SegmentedControl,
  SegmentedControlItem,
  Spacer,
  TextTitle,
} from '@pypestream/design-system';
import { FC, useState } from 'react';

import {
  getAnalyticsDashboardUrl,
  getAnalyticsProjectUrl,
} from '../utils/envs-related-data';
import { getEnvDotStyle } from '../utils/get-env-dot-color';
import { useProStudioCtxSelector } from '../xstate/app.xstate';

import './observe-stage.scss';

type Env = 'live' | 'sandbox';

interface PinnedDashboard {
  name: string;
  /** The dashboard's id in each environment's dataset. */
  ids: Record<Env, number>;
  createdBy: string;
  createdAt: string;
}

/**
 * The dashboards Analytics pins for a project, by project — hardcoded for the
 * demo. Their ids (and the Live/Sandbox dataset ids) differ per project, and
 * Pro Studio can't ask Analytics for them yet: its API answers browsers with
 * `Access-Control-Allow-Origin: *`, so a request carrying the user's session
 * is refused. Other projects show the empty row and the project link.
 */
const PINNED_DASHBOARDS: Record<
  string,
  { datasets: Record<Env, number>; dashboards: PinnedDashboard[] }
> = {
  // Bobcat Demo (candidate)
  'f3fc5055-44bb-4a3f-9119-f6a4c986e458': {
    datasets: { live: 13277, sandbox: 13278 },
    dashboards: [
      {
        name: 'Performance Overview',
        ids: { live: 496, sandbox: 499 },
        createdBy: 'Elana',
        createdAt: '2026-10-06',
      },
      {
        name: 'Engagement & Escalations',
        ids: { live: 497, sandbox: 500 },
        createdBy: 'Elana',
        createdAt: '2026-10-06',
      },
      {
        name: 'Engaged Users Transcripts',
        ids: { live: 498, sandbox: 501 },
        createdBy: 'Elana',
        createdAt: '2026-10-06',
      },
    ],
  },
};

const ENV_LABEL: Record<Env, string> = { live: 'Live', sandbox: 'Sandbox' };

const formatDate = (iso: string, month: 'long' | 'short') =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', {
    month,
    day: 'numeric',
    ...(month === 'long' ? { year: 'numeric' } : {}),
  });

/** Analytics marks dashboards with a blue panel glyph (prototype `AN_ICONS`). */
const DashboardGlyph: FC = () => (
  <svg
    viewBox="0 0 16 16"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-label="Dashboard"
  >
    <path d="M2.5 2.5h11v11h-11z" />
    <path d="M5 6h6M5 8.5h6M5 11h3.5" />
  </svg>
);

/**
 * Project Bobcat, 5-stage nav — the Observe stage (prototype `#stage-observe`).
 *
 * How the solution is doing lives in Analytics: the project's pinned
 * dashboards, listed the way Analytics lists them, where they're known
 * (`PINNED_DASHBOARDS`). A row opens that dashboard on the environment picked
 * in the card's head; "Open in Analytics" goes to the project there.
 */
export const ObserveStage: FC = () => {
  const { projectId, solutionName, liveVersion } = useProStudioCtxSelector(
    (ctx) => ({
      projectId: ctx.project?.id,
      solutionName: ctx.selectedBot?.displayName,
      liveVersion: ctx.deployedVersions?.live,
    })
  );
  // Live once something is live; until then the numbers are all in Sandbox.
  const [env, setEnv] = useState<Env>(liveVersion ? 'live' : 'sandbox');
  const name = solutionName || 'This solution';
  const analyticsUrl = projectId
    ? getAnalyticsProjectUrl(window.location.origin, projectId)
    : undefined;
  const pinned = projectId ? PINNED_DASHBOARDS[projectId] : undefined;

  return (
    <div className="c-observe-stage" data-testid="bobcat-observe-stage">
      <div className="c-observe-stage__main">
        <TextTitle size="small">Observe</TextTitle>
        <Spacer size="large" />
        <section className="c-observe-card" aria-label="Analytics">
          <div className="c-observe-card__head">
            <img src={logos.analytics} width={28} height={28} alt="" />
            <div className="c-observe-card__title">
              <span className="c-observe-card__name">Analytics</span>
              <span className="c-observe-card__sep" aria-hidden="true">
                /
              </span>
              <span className="c-observe-card__path">
                <svg
                  viewBox="0 0 16 16"
                  width="12"
                  height="12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  aria-hidden="true"
                >
                  <path d="M1.5 4.5v8a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-6.5a1 1 0 0 0-1-1H8L6.5 3h-4a1 1 0 0 0-1 1.5z" />
                </svg>
                {name} · {ENV_LABEL[env]}
              </span>
            </div>
            {pinned && (
              <SegmentedControl
                size="small"
                aria-label="Environment"
                value={env}
                onChange={(e) => setEnv(e.detail.value as Env)}
              >
                {(['sandbox', 'live'] as const).map((value) => (
                  <SegmentedControlItem key={value} value={value}>
                    <span
                      slot="prefix"
                      style={getEnvDotStyle(value, env === value)}
                    />
                    {ENV_LABEL[value]}
                  </SegmentedControlItem>
                ))}
              </SegmentedControl>
            )}
            {analyticsUrl && (
              <a
                className="c-observe-card__open"
                href={analyticsUrl}
                target="_blank"
                rel="noreferrer"
              >
                <span className="c-observe-card__open-text">
                  Open in Analytics
                </span>
                <Icon name="external-link" size="xsmall" />
              </a>
            )}
          </div>
          <div className="c-observe-table" role="table">
            <div
              className="c-observe-table__row c-observe-table__row--head"
              role="row"
            >
              <span role="columnheader">Type</span>
              <span role="columnheader">Name</span>
              <span role="columnheader" className="c-observe-table__edited">
                Created
              </span>
            </div>
            {pinned ? (
              pinned.dashboards.map((dashboard) => (
                <a
                  key={dashboard.name}
                  className="c-observe-table__row c-observe-table__row--item"
                  role="row"
                  href={getAnalyticsDashboardUrl(window.location.origin, {
                    projectId: projectId as string,
                    datasetId: pinned.datasets[env],
                    dashboardId: dashboard.ids[env],
                  })}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span role="cell" className="c-observe-table__type">
                    <DashboardGlyph />
                  </span>
                  <span role="cell" className="c-observe-table__name">
                    {dashboard.name}
                  </span>
                  <span role="cell" className="c-observe-table__edited">
                    <span className="c-observe-table__date">
                      {formatDate(dashboard.createdAt, 'long')}
                    </span>
                    <span className="c-observe-table__date c-observe-table__date--short">
                      {formatDate(dashboard.createdAt, 'short')}
                    </span>
                    <Avatar size="small" label={dashboard.createdBy} />
                  </span>
                </a>
              ))
            ) : (
              <div className="c-observe-table__empty" role="row">
                <span role="cell">
                  {name}’s dashboards aren’t listed here yet. Open Analytics to
                  see them.
                </span>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};
